/**
 * Organization Billing page — view current usage vs limits, buy/remove expansion packs.
 *
 * In DEMO mode (set by Platform Admin), "Buy pack" hits /confirm-demo and instantly adds packs.
 * In LIVE mode, it creates a Zoho Payments session + opens the Zoho Checkout widget
 * (`zpayments.js`). After payment success, the handler calls /packs/verify to validate.
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Package, Trash, Plus, Sparkle, CheckCircle } from "@phosphor-icons/react";

function loadZohoPaymentsScript() {
    return new Promise((resolve) => {
        if (window.ZPayments) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

function UsageRow({ label, used, cap }) {
    const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
    const danger = pct >= 90;
    const warn = !danger && pct >= 75;
    return (
        <div data-testid={`usage-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{label}</span>
                <span className="font-mono text-xs text-muted-foreground">
                    {typeof used === "number" ? used : "—"} / {cap}
                </span>
            </div>
            <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div
                    className={`h-full ${danger ? "bg-destructive" : warn ? "bg-yellow-500" : "bg-primary"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

export default function BillingPage() {
    const [snap, setSnap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buyDialog, setBuyDialog] = useState(false);
    const [packCount, setPackCount] = useState(1);
    const [busy, setBusy] = useState(false);
    const [removing, setRemoving] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/billing/me");
            setSnap(data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const buyPacks = async () => {
        if (!snap) return;
        setBusy(true);
        try {
            if (snap.payments?.demo_mode) {
                const { data } = await api.post("/billing/packs/confirm-demo", { pack_count: packCount });
                setSnap(data);
                toast.success(`${packCount} expansion pack(s) added (DEMO)`);
                setBuyDialog(false);
                return;
            }
            // LIVE flow — Zoho Payments
            const ok = await loadZohoPaymentsScript();
            if (!ok) { toast.error("Zoho Payments SDK failed to load"); return; }
            const { data: start } = await api.post("/billing/packs/start-purchase", { pack_count: packCount });
            if (!start.payment_session_id) {
                toast.error("Could not initiate payment");
                return;
            }
            const instance = new window.ZPayments({
                account_id: start.zoho_account_id,
                domain: "IN",
                otherOptions: { api_key: start.zoho_api_key },
            });
            try {
                const result = await instance.requestPaymentMethod({
                    amount: String(start.amount_inr),
                    transaction_type: "payment",
                    currency_code: "INR",
                    payments_session_id: start.payment_session_id,
                    currency_symbol: "₹",
                    business: "FormFlo",
                    description: `${packCount} × FormFlo expansion pack`,
                    invoice_number: start.purchase_id.slice(0, 24),
                });
                const { data: post } = await api.post("/billing/packs/verify", {
                    purchase_id: start.purchase_id,
                    payment_id: result.payment_id,
                    payment_session_id: start.payment_session_id,
                    signature: result.signature || null,
                });
                setSnap(post);
                toast.success("Payment captured. Packs added.");
                setBuyDialog(false);
            } catch (err) {
                if (err?.code !== "widget_closed") toast.error(err?.message || "Payment failed");
                else toast("Payment cancelled");
            } finally {
                try { await instance.close(); }
                catch (e) { console.warn("[billing] zoho instance close failed:", e); }
            }
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    const removePack = async () => {
        if (!removing) return;
        setBusy(true);
        try {
            const { data } = await api.post("/billing/packs/remove", { pack_id: removing.id });
            setSnap(data);
            toast.success("Pack removed");
            setRemoving(null);
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    if (loading || !snap) {
        return <PageContainer><div className="text-sm text-muted-foreground">Loading billing…</div></PageContainer>;
    }

    const { limits, usage, subscription, payments, pricing } = snap;
    const packs = subscription.expansion_packs || [];
    const overrideActive = Object.keys(limits.overrides || {}).length > 0;

    return (
        <PageContainer>
            <PageHeader
                overline="Billing"
                title="Subscription & usage"
                description="Track your plan, expand limits with packs, and view the current billing snapshot."
                actions={
                    <Button
                        onClick={() => { setPackCount(1); setBuyDialog(true); }}
                        className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="buy-pack-btn"
                    >
                        <Plus size={16} className="mr-2" /> Add expansion pack
                    </Button>
                }
                testid="billing-header"
            />

            {overrideActive && (
                <div className="mb-6 border border-yellow-500 bg-yellow-500/10 rounded-sm p-4 text-sm" data-testid="override-banner">
                    <div className="flex items-center gap-2 font-medium">
                        <Sparkle size={14} weight="fill" className="text-yellow-600" />
                        Platform Admin override is active
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                        {JSON.stringify(limits.overrides)}
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <div className="brut-section p-6 lg:col-span-2" data-testid="usage-section">
                    <div className="flex items-center gap-2 mb-6">
                        <CreditCard size={20} weight="duotone" />
                        <h2 className="text-xl font-bold tracking-tight">Current usage vs limits</h2>
                    </div>
                    <div className="space-y-5">
                        <UsageRow label="Users" used={usage.users} cap={limits.users} />
                        <UsageRow label="Forms" used={usage.forms} cap={limits.forms} />
                        <UsageRow label="Records" used={usage.records} cap={limits.records} />
                        <UsageRow label="Storage GB" used={usage.storage_gb?.toFixed?.(2) ?? 0} cap={limits.storage_gb} />
                    </div>
                </div>

                <div className="brut-section p-6" data-testid="plan-summary">
                    <div className="flex items-center gap-2 mb-6">
                        <Package size={20} weight="duotone" />
                        <h2 className="text-xl font-bold tracking-tight">Plan</h2>
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px] ml-1">Billed annually</Badge>
                    </div>
                    {(() => {
                        const monthly = pricing.expansion.monthly_inr;
                        const yearlyPerPack = monthly * 12;
                        const totalPacks = 1 + packs.length;
                        const total = yearlyPerPack * totalPacks;
                        const fmt = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
                        return (
                            <dl className="space-y-3 text-sm">
                                <div className="flex justify-between border-b border-border pb-3">
                                    <dt className="label-overline">Base · billed annually</dt>
                                    <dd className="font-mono">{fmt(yearlyPerPack)}/yr</dd>
                                </div>
                                <div className="flex justify-between border-b border-border pb-3">
                                    <dt className="label-overline">Packs · billed annually</dt>
                                    <dd className="font-mono">{packs.length} × {fmt(yearlyPerPack)}</dd>
                                </div>
                                <div className="flex justify-between pt-1">
                                    <dt className="label-overline text-base font-semibold">Total / year</dt>
                                    <dd className="font-mono font-semibold text-lg">{fmt(total)}</dd>
                                </div>
                            </dl>
                        );
                    })()}
                    <div className="mt-6 pt-4 border-t border-border">
                        <div className="label-overline mb-1">Payment mode</div>
                        <Badge className={`rounded-sm font-mono text-[10px] ${payments.demo_mode ? "bg-yellow-500" : ""}`} data-testid="payment-mode-badge">
                            {payments.demo_mode ? "DEMO" : "LIVE · Zoho Payments"}
                        </Badge>
                    </div>
                </div>
            </div>

            <div className="brut-section p-6" data-testid="packs-section">
                <h2 className="text-xl font-bold tracking-tight mb-4">Expansion packs</h2>
                {packs.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No expansion packs yet. Each pack adds +{pricing.expansion.users} users, +{pricing.expansion.forms} forms, +{pricing.expansion.records.toLocaleString("en-IN")} records, +{pricing.expansion.storage_gb} GB at ₹{(pricing.expansion.monthly_inr * 12).toLocaleString("en-IN")}/yr.</p>
                ) : (
                    <div className="space-y-2">
                        {packs.map((p, i) => (
                            <div key={p.id} className="flex items-center justify-between border border-border rounded-sm p-3 hover:bg-secondary/50" data-testid={`pack-row-${i}`}>
                                <div className="flex items-center gap-3">
                                    <div className="border border-foreground p-2 rounded-sm bg-foreground text-background">
                                        <Package size={16} weight="duotone" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-medium">Pack #{i + 1}</div>
                                        <div className="text-xs text-muted-foreground font-mono">
                                            {p.demo ? "DEMO · " : "LIVE · "}
                                            purchased {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString() : "—"}
                                            {p.zoho_payment_id && ` · ${p.zoho_payment_id.slice(0, 14)}…`}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive"
                                    onClick={() => setRemoving(p)} data-testid={`remove-pack-${i}`}
                                >
                                    <Trash size={14} className="mr-1" /> Remove
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Buy dialog */}
            <Dialog open={buyDialog} onOpenChange={setBuyDialog}>
                <DialogContent className="rounded-sm" data-testid="buy-pack-dialog">
                    <DialogHeader>
                        <DialogTitle>Add expansion pack(s)</DialogTitle>
                        <DialogDescription>
                            Each pack: +{pricing.expansion.users} users, +{pricing.expansion.forms} forms, +{pricing.expansion.records.toLocaleString("en-IN")} records, +{pricing.expansion.storage_gb} GB storage. Billed annually at ₹{(pricing.expansion.monthly_inr * 12).toLocaleString("en-IN")}/yr per pack.
                            {payments.demo_mode && <span className="block mt-2 font-medium text-yellow-700">DEMO mode is ON — no actual payment will be processed.</span>}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="label-overline">Number of packs</Label>
                            <Input
                                type="number" min={1} max={20} value={packCount}
                                onChange={(e) => setPackCount(Math.max(1, Number(e.target.value) || 1))}
                                className="mt-2 rounded-sm w-32" data-testid="pack-count-input"
                            />
                        </div>
                        {(() => {
                            const monthly = pricing.expansion.monthly_inr;
                            const total = monthly * 12 * packCount;
                            const fmt = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
                            return (
                                <div className="border border-border bg-secondary rounded-sm p-4 text-sm">
                                    <div className="flex justify-between font-semibold">
                                        <span>Total payable · billed annually</span>
                                        <span className="font-mono">{fmt(total)}/yr</span>
                                    </div>
                                    <div className="flex justify-between mt-3 text-xs text-muted-foreground">
                                        <span>New users cap</span>
                                        <span className="font-mono">{limits.users + pricing.expansion.users * packCount}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>New forms cap</span>
                                        <span className="font-mono">{limits.forms + pricing.expansion.forms * packCount}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>New records cap</span>
                                        <span className="font-mono">{(limits.records + pricing.expansion.records * packCount).toLocaleString("en-IN")}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-sm" onClick={() => setBuyDialog(false)} data-testid="cancel-buy-pack">Cancel</Button>
                        <Button
                            disabled={busy} onClick={buyPacks}
                            className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="confirm-buy-pack-btn"
                        >
                            {busy ? "Processing…" : payments.demo_mode ? "Confirm (demo)" : `Pay ₹${Math.round(pricing.expansion.monthly_inr * 12 * packCount).toLocaleString("en-IN")}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove confirmation */}
            <Dialog open={!!removing} onOpenChange={(v) => !v && setRemoving(null)}>
                <DialogContent className="rounded-sm" data-testid="remove-pack-dialog">
                    <DialogHeader>
                        <DialogTitle>Remove expansion pack?</DialogTitle>
                        <DialogDescription>
                            Removing this pack reduces your limits. The request is blocked if current usage would exceed the new limits.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-sm" onClick={() => setRemoving(null)} data-testid="cancel-remove-pack">Cancel</Button>
                        <Button
                            disabled={busy}
                            className="rounded-sm bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                            onClick={removePack} data-testid="confirm-remove-pack"
                        >
                            {busy ? "Removing…" : "Remove pack"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
